import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Target, 
  Star, 
  Clock, 
  Users, 
  CheckCircle, 
  AlertTriangle, 
  TrendingUp,
  Award,
  Lightbulb,
  BookOpen,
  Calendar,
  MessageSquare
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export const SuccessGuide: React.FC = () => {
  const { t } = useLanguage();
  
  const priorityCategories = [
    {
      id: 'high',
      title: t("highPriority") || 'High Priority',
      icon: Target,
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-50/80 dark:bg-red-900/20',
      borderColor: 'border-red-200 dark:border-red-800',
      items: [
        {
          category: t("ovitEvaluations") || 'OVIT Evaluations',
          points: `0-80 ${t("points") || "points"}`,
          weight: `80% ${t("ofBaseScore") || "of base score"}`,
          description: t("mostImportantPart") || 'The most important part of your evaluation',
          tips: [
            t("prepareWellForOVIT") || 'Prepare well for each OVIT evaluation',
            t("studyCriteriaBeforehand") || 'Study evaluation criteria beforehand',
            t("askFeedbackFromSupervisors") || 'Ask for feedback from your supervisors',
            t("applyRecommendations") || 'Apply recommendations from previous evaluations'
          ]
        }
      ]
    },
    {
      id: 'medium',
      title: t("mediumPriority") || 'Medium Priority',
      icon: Star,
      color: 'text-yellow-600 dark:text-yellow-400',
      bgColor: 'bg-yellow-50/80 dark:bg-yellow-900/20',
      borderColor: 'border-yellow-200 dark:border-yellow-800',
      items: [
        {
          category: t("behaviorsCategory") || 'Behaviors',
          points: t("upTo15Points") || 'up to ±15 points',
          weight: t("largeImpactOnFinalScore") || 'Large impact on final score',
          description: t("positiveAndNegativeBehaviors") || 'Positive and negative behaviors',
          tips: [
            t("avoidNegativeBehaviors") || 'Avoid negative behaviors (delays, omissions)',
            t("cultivatePositiveBehaviors") || 'Cultivate positive behaviors (proactivity, cooperation)',
            t("participateActivelyInTeam") || 'Participate actively in team activities',
            t("showInitiativeAndResponsibility") || 'Show initiative and responsibility'
          ]
        }
      ]
    },
    {
      id: 'low',
      title: t("lowPriority") || 'Low Priority',
      icon: CheckCircle,
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50/80 dark:bg-green-900/20',
      borderColor: 'border-green-200 dark:border-green-800',
      items: [
        {
          category: t("operationalActivityCategory") || 'Operational Activity',
          points: `${t("upTo") || "up to"} 10 ${t("points") || "points"}`,
          weight: t("supplementaryPoints") || 'Supplementary points',
          description: t("dailyActivitiesAndTasks") || 'Daily activities and tasks',
          tips: [
            t("completeAllDailyTasks") || 'Complete all daily tasks',
            t("recordYourActivities") || 'Record your activities',
            t("participateInAdditionalTasks") || 'Participate in additional tasks when requested',
            t("maintainGoodWorkRecords") || 'Maintain good work records'
          ]
        },
        {
          category: t("supervisorReportsCategory") || 'Supervisor Reports',
          points: `±10 ${t("points") || "points"}`,
          weight: t("feedbackFromManagers") || 'Feedback from managers',
          description: t("evaluationFromSupervisors") || 'Evaluation from your supervisors',
          tips: [
            t("maintainGoodCommunication") || 'Maintain good communication with supervisors',
            t("askForRegularFeedback") || 'Ask for regular feedback',
            t("applyReceivedRecommendations") || 'Apply the recommendations you receive',
            t("showYouLearnFromMistakes") || 'Show that you learn from your mistakes'
          ]
        },
        {
          category: t("clientEvaluationsCategory") || 'Client Evaluations',
          points: `${t("upTo") || "up to"} 5 ${t("points") || "points"}`,
          weight: t("clientSatisfaction") || 'Client satisfaction',
          description: t("feedbackFromClients") || 'Feedback from clients',
          tips: [
            t("maintainProfessionalBehavior") || 'Maintain professional behavior',
            t("respondQuicklyToClientNeeds") || 'Respond quickly to client needs',
            t("communicateEffectively") || 'Communicate effectively',
            t("solveProblemsProactively") || 'Solve problems proactively'
          ]
        }
      ]
    }
  ];

  const monthlyActionPlan = [
    {
      period: t("week12") || 'Week 1-2',
      title: t("foundation") || 'Foundation',
      actions: [
        t("reviewOVITCriteria") || 'Review OVIT evaluation criteria',
        t("setPersonalPerformanceGoals") || 'Set personal performance goals',
        t("discussExpectationsWithSupervisor") || 'Discuss expectations with supervisor'
      ]
    },
    {
      period: t("week34") || 'Week 3-4',
      title: t("implementation") || 'Implementation',
      actions: [
        t("applyBestPractices") || 'Apply best practices',
        t("recordPositiveActivities") || 'Record positive activities',
        t("avoidNegativeBehaviorsAction") || 'Avoid negative behaviors'
      ]
    },
    {
      period: t("month23") || 'Month 2-3',
      title: t("optimization") || 'Optimization',
      actions: [
        t("evaluateProgress") || 'Evaluate progress',
        t("adjustStrategy") || 'Adjust strategy',
        t("seekAdditionalImprovementOpportunities") || 'Seek additional improvement opportunities'
      ]
    },
    {
      period: t("month4Plus") || 'Month 4+',
      title: t("maintainExcellence") || 'Maintain Excellence',
      actions: [
        t("continuousImprovement") || 'Continuous improvement',
        t("mentorNewColleagues") || 'Mentor new colleagues',
        t("targetDistinctionBadge") || 'Target Distinction Badge (100+)'
      ]
    }
  ];

  const behaviorExamples = {
    positive: [
      { behavior: t("timelyArrivalAtWork") || 'Timely arrival at work', impact: '+2 points' },
      { behavior: t("initiativeInEmergencies") || 'Initiative in emergencies', impact: '+3 points' },
      { behavior: t("helpingColleagues") || 'Helping colleagues', impact: '+1 point' },
      { behavior: t("proactiveReportingOfProblems") || 'Proactive reporting of problems', impact: '+2 points' },
      { behavior: t("participationInTraining") || 'Participation in training', impact: '+1 point' }
    ],
    negative: [
      { behavior: t("delayAtWork") || 'Delay at work', impact: '-2 points' },
      { behavior: t("omissionOfDuties") || 'Omission of duties', impact: '-3 points' },
      { behavior: t("nonCooperativeAttitude") || 'Non-cooperative attitude', impact: '-1 point' },
      { behavior: t("violationOfRegulations") || 'Violation of regulations', impact: '-5 points' },
      { behavior: t("clientComplaints") || 'Client complaints', impact: '-2 points' }
    ]
  };

  const distinctionStrategy = [
    {
      title: t("step1SecureSolidFoundation") || 'Step 1: Secure Solid Foundation',
      target: t("targetBaseScore75Plus") || '75+ points in base score',
      actions: [
        t("achieveExcellentOVITPerformance") || 'Achieve excellent performance in OVIT evaluations',
        t("maintainConsistentDailyPerformance") || 'Maintain consistent daily performance',
        t("avoidAllNegativeBehaviors") || 'Avoid all negative behaviors'
      ]
    },
    {
      title: t("step2CollectBonusPoints") || 'Step 2: Collect Bonus Points',
      target: t("target1525ExtraPoints") || '15-25 additional points',
      actions: [
        t("accumulatePositiveBehaviors") || 'Accumulate positive behaviors',
        t("secureExcellentClientEvaluations") || 'Secure excellent client evaluations',
        t("participateInAllOperationalActivities") || 'Participate in all operational activities'
      ]
    },
    {
      title: t("step3TargetExcellence") || 'Step 3: Target Excellence',
      target: t("target100PlusTotal") || '100+ points total',
      actions: [
        t("becomeRoleModel") || 'Become a role model for others',
        t("takeLeadershipRole") || 'Take a leadership role',
        t("contributeToProcessImprovement") || 'Contribute to process improvement'
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-foreground mb-2">
          {t("successGuideTitle") || "Evaluation Success Guide"}
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {t("successGuideDescription") || "The complete guide to achieving excellent evaluation in the 100-point system. Learn where to focus and how to improve your performance."}
        </p>
      </div>

      <Tabs defaultValue="priorities" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="priorities">{t("priorities") || "Priorities"}</TabsTrigger>
          <TabsTrigger value="behaviors">{t("behaviors") || "Behaviors"}</TabsTrigger>
          <TabsTrigger value="timeline">{t("timeline") || "Timeline"}</TabsTrigger>
          <TabsTrigger value="distinction">{t("distinction") || "Distinction"}</TabsTrigger>
          <TabsTrigger value="tips">{t("tips") || "Tips"}</TabsTrigger>
        </TabsList>

        <TabsContent value="priorities" className="space-y-6">
          {priorityCategories.map(category => (
            <Card key={category.id} className={`${category.borderColor}`}>
              <CardHeader className={`${category.bgColor}`}>
                <CardTitle className={`flex items-center gap-2 ${category.color}`}>
                  {React.createElement(category.icon, { className: "h-5 w-5" })}
                  {category.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {category.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="space-y-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-foreground">{item.category}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className={category.id === 'high' ? 'border-red-300 dark:border-red-700' : category.id === 'medium' ? 'border-yellow-300 dark:border-yellow-700' : 'border-green-300 dark:border-green-700'}>
                            {item.points}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">{item.weight}</p>
                        </div>
                      </div>
                      <ul className="space-y-2 ml-4">
                        {item.tips.map((tip, tipIndex) => (
                          <li key={tipIndex} className="flex items-start gap-2 text-sm text-foreground">
                            <CheckCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${category.id === 'high' ? 'text-red-500 dark:text-red-400' : category.id === 'medium' ? 'text-yellow-500 dark:text-yellow-400' : 'text-green-500 dark:text-green-400'}`} />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="behaviors" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-green-200 dark:border-green-800">
              <CardHeader className="bg-green-50/80 dark:bg-green-900/20">
                <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <CheckCircle className="h-5 w-5" />
                  {t("positiveBehaviorsTitle") || "Positive Behaviors"}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {behaviorExamples.positive.map((example, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-green-50/80 dark:bg-green-900/20 rounded-lg">
                      <span className="text-sm text-foreground">{example.behavior}</span>
                      <Badge variant="secondary" className="bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300">
                        {example.impact}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200 dark:border-red-800">
              <CardHeader className="bg-red-50/80 dark:bg-red-900/20">
                <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                  <AlertTriangle className="h-5 w-5" />
                  {t("negativeBehaviorsTitle") || "Negative Behaviors"}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {behaviorExamples.negative.map((example, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-red-50/80 dark:bg-red-900/20 rounded-lg">
                      <span className="text-sm text-foreground">{example.behavior}</span>
                      <Badge variant="secondary" className="bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-300">
                        {example.impact}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="timeline" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {t("monthlyActionPlan") || "Monthly Action Plan"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {monthlyActionPlan.map((phase, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold">
                        {index + 1}
                      </div>
                      {index < monthlyActionPlan.length - 1 && (
                        <div className="w-0.5 h-20 bg-border mt-2" />
                      )}
                    </div>
                    <div className="flex-1 pb-8">
                      <div className="flex items-baseline gap-3 mb-2">
                        <h4 className="font-semibold text-foreground">{phase.period}</h4>
                        <Badge>{phase.title}</Badge>
                      </div>
                      <ul className="space-y-2">
                        {phase.actions.map((action, actionIndex) => (
                          <li key={actionIndex} className="flex items-start gap-2 text-sm text-foreground">
                            <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                            {action}
                          </li>
                        ))}
                      </ul>
                   </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="distinction" className="space-y-6">
          <Card className="border-yellow-200 dark:border-yellow-800">
            <CardHeader className="bg-yellow-50/80 dark:bg-yellow-900/20">
              <CardTitle className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                <Award className="h-5 w-5" />
                {t("strategyForDistinctionBadge") || "Strategy for Distinction Badge (100+ points)"}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                {distinctionStrategy.map((step, index) => (
                  <div key={index} className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-500 dark:bg-yellow-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">{step.title}</h4>
                        <Badge variant="outline" className="mt-1">
                          {step.target}
                        </Badge>
                      </div>
                    </div>
                    <div className="ml-11 space-y-2">
                      {step.actions.map((action, actionIndex) => (
                        <div key={actionIndex} className="flex items-start gap-2 text-sm text-foreground">
                          <TrendingUp className="h-4 w-4 text-yellow-500 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                          {action}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("pointsDistributionForDistinction") || "Points Distribution for Distinction"}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t("ovitEvaluationTarget75Plus") || "OVIT Evaluation (Target: 75+)"}</span>
                    <span className="font-medium">75/80</span>
                  </div>
                  <Progress value={93.75} className="h-2" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t("positiveBehaviorsTarget10Plus") || "Positive Behaviors (Target: 10+)"}</span>
                    <span className="font-medium">12/15</span>
                  </div>
                  <Progress value={80} className="h-2" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t("operationalActivityTarget8Plus") || "Operational Activity (Target: 8+)"}</span>
                    <span className="font-medium">9/10</span>
                  </div>
                  <Progress value={90} className="h-2" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t("clientEvaluationsTarget4Plus") || "Client Evaluations (Target: 4+)"}</span>
                    <span className="font-medium">5/5</span>
                  </div>
                  <Progress value={100} className="h-2" />
                </div>
                <div className="pt-2 border-t">
                  <div className="flex justify-between font-semibold">
                    <span>{t("totalTargetScore") || "Total Target Score"}</span>
                    <span className="text-yellow-600">101/110</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tips" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5" />
                  {t("generalTips") || "General Tips"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    {t("monitorYourProgressRegularly") || "Monitor your progress regularly"}
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    {t("askFeedbackFromSupervisorsAndColleagues") || "Ask for feedback from supervisors and colleagues"}
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    {t("focusOnHighestImpactAreas") || "Focus on areas with the highest impact"}
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    {t("maintainConsistentPerformance") || "Maintain consistent performance throughout the period"}
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  {t("improvementResources") || "Improvement Resources"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <MessageSquare className="h-4 w-4 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    {t("participateInTrainingPrograms") || "Participate in training programs"}
                  </li>
                  <li className="flex items-start gap-2">
                    <Users className="h-4 w-4 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    {t("seekMentorOrExperiencedColleague") || "Seek a mentor or experienced colleague"}
                  </li>
                  <li className="flex items-start gap-2">
                    <BookOpen className="h-4 w-4 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    {t("studyIndustryBestPractices") || "Study industry best practices"}
                  </li>
                  <li className="flex items-start gap-2">
                    <Clock className="h-4 w-4 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    {t("manageYourTimeEffectively") || "Manage your time effectively"}
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="text-center">
                <Award className="h-12 w-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-foreground">{t("remember") || "Remember"}</h3>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  {t("successIsNotJustAboutPoints") || "Success in evaluation is not just about points - it's about continuous improvement and growth as a professional. Each step you take brings you closer to excellence."}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};